import type { FC } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Form from '@components/Form';
import Modal from '@components/Modal';
import { Show, ShowFirstMatching } from '@components/Show';
import { Stepper } from '@components/Stepper';
import { useEntityStore } from '@ContractBuilder/store';
import { fetchSchemaIfNotAvailableInStore } from '@ContractBuilder/store/utils';
import type { EndorsementData } from '@ContractBuilder/types';
import { canChange } from '@helpers/canChange';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useEndorsementSummaryTemplates } from '@src/queries';
import clsx from 'clsx';
import { get } from 'lodash-es';

import { formContainerClasses } from '../classes';
import { useEndorsementForm, useEndorsementMutation, useParentSubmission, useStepper } from '../hooks';
import { EndorsementStep } from '../types';
import { FormFooter, Step0, Step0OpenSearch, Step1, Step2, Step3 } from '../views';

interface EndorsementModalControllerProps {
  handleClose: () => void;
  isEdit?: boolean;
  isStandalone?: boolean;
}

export const EndorsementModalController: FC<EndorsementModalControllerProps> = ({
  handleClose,
  isEdit = false,
  isStandalone = false,
}) => {
  const getFeatureFlag = useFeatureFlags();
  const isOpenSearchEnabled = getFeatureFlag('OpenSearch');
  const storeSubmission = useEntityStore(({ submission }) => submission);
  const urlSubmissionId = useParams().id;

  const totalSteps = isStandalone ? 4 : 3;
  const { currentStep, getStepperItemState, onNext, onPrevious } = useStepper({
    totalSteps,
    onClose: handleClose,
  });

  const { control, formValues, handleSubmit, isValid, setValue } = useEndorsementForm(
    storeSubmission as EndorsementData,
    isEdit,
    isStandalone,
    currentStep,
  );

  const { data: endorsementSummaryTemplates, isFetching: isFetchingEndorsementSummaryTemplates } =
    useEndorsementSummaryTemplates(currentStep === EndorsementStep.SummaryTemplate);

  const { submission: parentSubmission } = useParentSubmission(formValues.parentSubmissionId, isStandalone);

  const entity = (isStandalone ? parentSubmission : storeSubmission) as EndorsementData;
  const submissionId = isStandalone ? formValues.parentSubmissionId : urlSubmissionId;

  const {
    onCreate,
    onUpdate,
    isLoading: isMutating,
  } = useEndorsementMutation({
    submissionId,
    onSuccess: handleClose,
  });

  useEffectOnce(() => {
    if (isStandalone) {
      fetchSchemaIfNotAvailableInStore();
    }
  });

  useEffect(() => {
    if (entity && !formValues.expiry_date) {
      const period = get(entity, 'data_items.risk_details.period[0]');
      const expiry_date = get(period, 'period_matrix.to.date');

      if (expiry_date) {
        setValue('expiry_date', expiry_date);
      }
    }
  }, [entity, isStandalone, formValues.expiry_date, setValue]);

  const canEditEndorsement = !isEdit || canChange(entity?.status);
  const modalTitle = isEdit ? 'Edit endorsement' : 'Create an endorsement';

  const stepProps = {
    control,
    formValues,
    isReadOnly: !canEditEndorsement,
    setValue,
  };

  return (
    <Modal
      className="w-lg"
      onClose={handleClose}
      open
      shouldShowCloseIcon={false}
      title={modalTitle}
      titleClassName="text-lg leading-6 font-medium justify-center!"
    >
      <Form
        className={clsx(formContainerClasses, 'h-full')}
        onSubmit={handleSubmit(isEdit ? onUpdate : onCreate)}
        onKeyDown={preventDefaultEnterSubmit}
      >
        <p className="text-center text-sm leading-5 font-normal text-gray-500">
          {isEdit
            ? 'Please update any information to edit the endorsement'
            : 'Please fill in the following information to setup an endorsement'}
        </p>
        <div className="py-4">
          <Stepper getStepperItemState={getStepperItemState} size={totalSteps} />
        </div>
        <div className="flex h-full w-full flex-col gap-6">
          <ShowFirstMatching>
            <Show when={isStandalone && currentStep === EndorsementStep.ParentSelection}>
              {isOpenSearchEnabled ? <Step0OpenSearch {...stepProps} /> : <Step0 {...stepProps} />}
            </Show>
            <Show when={currentStep === EndorsementStep.SummaryTemplate}>
              <Step1
                {...stepProps}
                isReadOnly={isEdit || stepProps.isReadOnly}
                isFetchingEndorsementSummaryTemplates={isFetchingEndorsementSummaryTemplates}
                endorsementSummaryTemplates={(endorsementSummaryTemplates ?? []).map((i) => ({
                  value: i.id,
                  name: i.title,
                }))}
              />
            </Show>
            <Show when={currentStep === EndorsementStep.BasicInfo}>
              <Step2 {...stepProps} />
            </Show>
            <Show when={currentStep === EndorsementStep.ReasonPremium}>
              <Step3 {...stepProps} />
            </Show>
          </ShowFirstMatching>
          <FormFooter
            currentStep={currentStep}
            isValid={isValid}
            isLoading={isMutating}
            isEdit={isEdit}
            isStandalone={isStandalone}
            onNext={onNext}
            onPrevious={onPrevious}
          />
        </div>
      </Form>
    </Modal>
  );
};
